/* S01 — Sign in.
 *
 * "Split field. Ink panel left carries the mark and one measured proof point;
 * form right, never centred on a photograph." Product Screens, slide 10.
 *
 * The email and password fields the deck draws are not buildable and were
 * removed. Sign-in is redirect-based OIDC: the user authenticates on Microsoft's
 * page and returns with a token, so this app never sees a credential
 * (`02` Identity, `14` section 9.2 which flagged the screen as undrawable as
 * specified). A form here would mean the resource-owner password flow, which
 * hands us the password, is disabled by default in Entra, and is the thing that
 * decision exists to prevent.
 *
 * The split panel, the mark and the proof point all survive. What changed is the
 * middle of the right-hand side.
 *
 * **One button per directory this build carries**, because ACRT staff and a
 * utility's people sign in against different directories (`02` Identity). The
 * deck's one-primary-action rule survives rather than bending: the first
 * configured directory takes `btn--primary` and the rest are secondary
 * (Product Screens slide 5, rule 01). A build carrying one directory shows one
 * button, so the ordinary single-tenant case still looks as drawn.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  account,
  authConfigured,
  authFailureMessage,
  configuredDirectories,
  ready,
  signIn,
  type DirectoryId,
} from "../auth";
import { Logo } from "../components/Logo";
import { invitedSignIn } from "../lib/invitedSignIn";
import s from "./SignIn.module.css";

/* What each front door is called on screen.
 *
 * Which one is primary is NOT decided here: the first entry of
 * `configuredDirectories` takes `btn--primary` and `auth.ts` sets that order,
 * with the argument for it. Naming a directory here as well would be a second
 * statement of one rule, and the two would disagree the day either moved.
 *
 * Where a build carries only one directory there is no choice to present, and
 * the label says "Sign in" rather than naming a distinction the user cannot
 * act on.
 */
const LABEL: Record<DirectoryId, string> = {
  external: "Sign in",
  workforce: "ACRT staff sign-in",
};

export default function SignIn() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [checking, setChecking] = useState(true);

  // A signed-in user landing on the sign-in screen goes to the app rather than
  // being asked to sign in again. This is a subscription to MSAL's settled
  // state, not a data fetch, so an effect is the right place for it
  // (`CLAUDE.md` section 4).
  useEffect(() => {
    let cancelled = false;
    ready()
      .then(() => {
        if (cancelled) return;
        if (account()) {
          navigate("/app", { replace: true });
          return;
        }
        // An invitation's link names the directory and the address, so the
        // invitee goes straight to it rather than choosing a button.
        const invited = invitedSignIn(search, configuredDirectories);
        if (invited) {
          // Out of the history, so Back from Microsoft's page shows the
          // buttons rather than redirecting again. Not through navigate,
          // whose new search would re-run this effect.
          window.history.replaceState(null, "", "/");
          void onSignIn(invited.directory, invited.loginHint);
        } else {
          setChecking(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setNotice(authFailureMessage(err));
          setChecking(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, search]);

  async function onSignIn(directory: DirectoryId, loginHint?: string) {
    setPending(true);
    setNotice(null);
    try {
      await signIn(directory, loginHint);
    } catch (err: unknown) {
      // The same builder as the load path above. Both are MSAL failures and the
      // error code is what tells them apart, so a second hand-written sentence
      // here would differ from that one only by being less informative.
      setNotice(authFailureMessage(err));
      setPending(false);
      setChecking(false);
    }
  }

  // Nothing until MSAL settles. A sign-in returning from Entra lands here to
  // finish the exchange (AuthCallback.tsx) and goes straight on to the app, so
  // drawing the screen first would flash it between Entra and the app.
  if (checking) return null;

  return (
    <div className={s["signin"]}>
      <aside className={s["panel"]}>
        <div className={s["mark"]}>
          <Logo height={32} />
        </div>

        <p className={s["claim"]}>
          Every span measured. Every decision defensible.
        </p>

        <p className={`mono ${s["proof"]}`}>
          12,400 spans · 890 circuit miles · capture 12 Mar 2026
        </p>

        <p className={`mono ${s["family"]}`}>Part of the ACRT family of companies</p>
      </aside>

      <main className={s["formField"]}>
        <div className={s["form"]}>
          <h1 className="auth__title">Sign in</h1>
          <p className={`auth__lede ${s["lede"]}`}>
            You will be taken to your organisation's sign-in page. Access is
            granted by your administrator.
          </p>

          {/* One primary action per view - Product Screens, slide 5, rule 01.
            * The first configured directory takes it and the rest are
            * secondary, so a build carrying both offers a choice and a build
            * carrying one offers a button. */}
          {configuredDirectories.map((directory, index) => (
            <button
              key={directory}
              className={`btn ${index === 0 ? "btn--primary" : "btn--secondary"}`}
              type="button"
              onClick={() => void onSignIn(directory)}
              disabled={pending}
            >
              {pending
                ? "Redirecting…"
                : configuredDirectories.length === 1
                  ? "Sign in"
                  : LABEL[directory]}
            </button>
          ))}

          {!authConfigured && (
            <p className="notice" role="status">
              This build carries no identity configuration, so sign-in is
              unavailable. It is not misconfigured credentials — the client id,
              directory id and API scope were absent at build time.
            </p>
          )}

          {notice && (
            <p className="notice" role="status">
              {notice}
            </p>
          )}

          <p className={s["terms"]}>
            Sessions expire after 12 hours. Measurement data is scoped to your
            organisation.
          </p>
        </div>
      </main>
    </div>
  );
}
